// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import {
    Card,
    CircularProgress,
    Fade,
    Grid,
    Icon,
    Modal,
    Tooltip
} from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { getColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalNuevoPermisoCampo({ open, handleClose, idPermiso, refetch }) {
  const { handleSnackbar } = useContext(MessageManager);
  const inputRefs = useRef([]);
  const [permisosCampo, setPermisosCampo] = useState([]);

  const [addPermisoFile, { loading: loadingFile }] = useMutation(gql`
    mutation addPermisoFile(
      $idPermiso: ID!
      $file: Upload!
      $nombre: String!
      $ocrId: String
      $tipo: String
    ) {
      addPermisoFile(
        idPermiso: $idPermiso
        file: $file
        nombre: $nombre
        ocrId: $ocrId
        tipo: $tipo
      ) {
        id
      }
    }
  `);

  useEffect(() => {
    if (idPermiso && open) {
      setPermisosCampo([
        { id: new Date().getTime(), nombre: "", file: null },
      ]);
    }
  }, [idPermiso, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Agregar permisos de campo</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox>
              <SoftBox py={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SoftBox display="flex" flexDirection="column" gap={1}>
                      <SoftBox display="flex" justifyContent="space-between">
                        <SoftBox display="flex" gap={1} alignItems="center">
                          <Icon
                            sx={{
                              color: getColor("gobSecondary"),
                            }}
                          >
                            article
                          </Icon>
                          <SoftTypography variant="h7" fontWeight="bold">
                            Permisos de campo
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      <SoftTypography variant="h7">
                        Permisos de campo firmados por la policía o un escribano por cada campo en
                        el que vaya a cazar.
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                  {permisosCampo.map((permiso, index) => (
                    <Grid item xs={12} key={permiso.id}>
                      <SoftBox display="flex" gap={2}>
                        <SoftInput
                          placeholder="Ingrese el nombre del campo"
                          value={permiso.nombre}
                          onChange={(e) => {
                            const newPermisosCampo = permisosCampo.map((p) => {
                              if (p.id === permiso.id) {
                                return {
                                  ...p,
                                  nombre: e.target.value,
                                };
                              }
                              return p;
                            });
                            setPermisosCampo(newPermisosCampo);
                          }}
                        />
                        <SoftButton
                          color={permiso.file ? "success" : "gobSecondary"}
                          iconOnly
                          onClick={() => inputRefs.current[permiso.id].click()}
                          disabled={loadingFile}
                        >
                          <Icon sx={{ fontWeight: "light" }}>
                            {permiso.file ? "check" : "cloud_upload"}
                          </Icon>
                        </SoftButton>
                        <SoftButton
                          color="error"
                          iconOnly
                          disabled={permisosCampo.length === 1}
                          onClick={() => {
                            setPermisosCampo(permisosCampo.filter((p) => p.id !== permiso.id));
                          }}
                        >
                          <Icon sx={{ fontWeight: "light" }}>close</Icon>
                        </SoftButton>
                      </SoftBox>
                      <input
                        ref={(el) => (inputRefs.current[permiso.id] = el)}
                        type="file"
                        style={{ display: "none" }}
                        accept=".pdf, .jpg, .jpeg, .png"
                        onChange={(e) => {
                          if (e.target.files[0] && e.target.files[0].size < 5000000) {
                            const newPermisosCampo = permisosCampo.map((p) => {
                              if (p.id === permiso.id) {
                                return {
                                  ...p,
                                  file: e.target.files[0],
                                };
                              }
                              return p;
                            });
                            setPermisosCampo(newPermisosCampo);
                          } else {
                            handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                          }
                        }}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Tooltip title="Agregar permiso de campo" placement="top">
                      <SoftButton
                        color="gobSecondary"
                        fullWidth
                        onClick={() => {
                          setPermisosCampo([
                            ...permisosCampo,
                            { id: new Date().getTime(), nombre: "", file: null },
                          ]);
                        }}
                        disabled={loadingFile || permisosCampo.length >= 8}
                      >
                        Agregar permiso de campo&nbsp;
                        <Icon sx={{ fontWeight: "light" }}>add</Icon>
                      </SoftButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </SoftBox>
              <SoftBox>
                  <SoftButton
                    color="success"
                    fullWidth
                    disabled={
                      loadingFile || !idPermiso || permisosCampo.find((p) => !p.file || !p.nombre)
                    }
                    onClick={() => {
                      if (permisosCampo.length) {
                        const promises = permisosCampo.map((f) =>
                          addPermisoFile({
                            variables: {
                              idPermiso,
                              file: f.file,
                              nombre: f.nombre,
                              tipo: "Permiso de campo",
                            },
                          })
                        );
                        Promise.all(promises)
                          .then(() => {
                            handleSnackbar("Archivos subidos correctamente", "success");
                            setPermisosCampo([]);
                            handleClose();
                            refetch();
                          })
                          .catch(() => {
                            handleSnackbar("Error al subir los archivos", "error");
                          });
                      }
                    }}
                  >
                    Guardar&nbsp;
                    {loadingFile ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          marginRight: 0.5,
                        }}
                        color="inherit"
                      />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                  </SoftButton>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevoPermisoCampo.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPermiso: PropTypes.string,
  refetch: PropTypes.func,
};
